import { axiosInstance } from '../../../common/axiosInstans';
import type {
    ApiErrorResponse,
    AuthCredentials,
    RenewSecureKeyRequest,
    RevokeSecureKeyRequest,
    SecureKeyResponse,
    SecureKeyStatusResponse,
} from '../../../types/secureKey';

export const secureKeyApi = {
    authenticate: async (
        credentials: AuthCredentials
    ): Promise<SecureKeyResponse> => {
        try {
            const response = await axiosInstance.post(
                '/securekey/authenticate',
                credentials,
                {
                    skipAuthRedirect: true, // Don't logout user on 401 from authentication
                }
            );
            return response.data;
        } catch (error: any) {
            // Handle rate limiting and other API errors
            if (error.response?.status === 429) {
                const errorData: ApiErrorResponse = error.response.data;
                throw new Error(
                    `För många försök. Försök igen om ${errorData.retryAfter || 60} sekunder.`
                );
            }
            if (error.response?.status === 401) {
                throw new Error(
                    'Ogiltiga uppgifter. Kontrollera din e-post/användarnamn och lösenord.'
                );
            }
            if (error.response?.status === 403) {
                throw new Error(
                    'Åtkomst nekad. Du har inte behörighet att komma åt patientdata.'
                );
            }
            throw new Error(
                error.response?.data?.error ||
                    'Autentisering misslyckades. Försök igen.'
            );
        }
    },

    renew: async (currentKey: string): Promise<SecureKeyResponse> => {
        try {
            const request: RenewSecureKeyRequest = { secureKey: currentKey };
            const response = await axiosInstance.post(
                '/securekey/renew',
                request
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 401) {
                throw new Error(
                    'Säkerhetsnyckeln är ogiltig eller har gått ut. Autentisera dig igen.'
                );
            }
            if (error.response?.status === 403) {
                throw new Error(
                    'Kan inte förnya säkerhetsnyckeln. Autentisera dig igen.'
                );
            }
            throw new Error(
                error.response?.data?.error ||
                    'Kunde inte förnya säkerhetsnyckeln. Försök igen.'
            );
        }
    },

    /**
     * Revoke a secure key manually
     */
    revoke: async (secureKey: string): Promise<void> => {
        try {
            const request: RevokeSecureKeyRequest = { secureKey };
            await axiosInstance.delete('/securekey/revoke', { data: request });
        } catch (error: any) {
            // Don't throw errors for revoke - it's a cleanup operation
            console.warn(
                'Failed to revoke secure key:',
                error.response?.data?.error || error.message
            );
        }
    },

    getStatus: async (secureKey?: string): Promise<SecureKeyStatusResponse> => {
        try {
            const params = secureKey ? { secureKey } : {};
            const response = await axiosInstance.get('/securekey/status', {
                params,
                skipAuthRedirect: true, // Don't logout user on 401 from status check
            });
            return response.data;
        } catch (error: any) {
            // Return default status if check fails
            return {
                hasActiveKey: false,
                expiresAt: null,
                expiresInMinutes: 0,
                isExpiringSoon: false,
            };
        }
    },
};

export const addSecureKeyHeader = (secureKey: string | null) => {
    if (secureKey) {
        return { 'X-Secure-Key': secureKey };
    }
    return {};
};
